import React, { Component } from "react";
import { Link, NavLink } from "react-router-dom";

// CSS
import "./Header.scss";

// Components
import Icon from "../Icon";

// Helper
import { getCurrentUser, clearSession } from "../../Helper/Api";
import { brandingOf, hasAbout } from "../../Helper/Branding";
import { hasModule } from "../../Helper/modules";
import { t } from "../../Helper/i18n";
import { endPreview, isEmbedded } from "../../Helper/Theme";

/** Two letters for the avatar: first and last word of the name. */
function initialsOf(name) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  const first = words[0][0] || "";
  const last = words.length > 1 ? words[words.length - 1][0] : "";
  return (first + last).toUpperCase();
}

/* The two states read differently on purpose: a guest sees the way in (a
 * quiet sign-in and the gold sign-up), a member sees who they are signed in as
 * (the avatar chip, its menu) and the member pages after a divider.
 *
 * Everything added here is presentation: the glider that follows the pointer,
 * the condensed bar once the page scrolls, the account menu. The links, their
 * conditions and logout are the ones the header always had. */
class Header extends Component {
  state = {
    currentUser: getCurrentUser(),
    mobileOpen: false,
    scrolled: false,
    menuOpen: false,
  };

  nav = React.createRef();
  glide = React.createRef();
  account = React.createRef();
  bar = React.createRef();

  // Every page mounts its own Header, so an entrance on each mount would replay
  // on every link and read as a reload. It plays once per visit.
  entering = !Header.entered;

  componentDidMount() {
    Header.entered = true;
    // the bar's own height, as --hdr-h on <html>: themes whose first screen runs up under a see-through bar pull it
    // up by exactly this much. Read only at the top of the page, before the bar condenses on scrolling.
    if ("ResizeObserver" in window && this.bar.current) {
      this.ro = new ResizeObserver(() => {
        if (this.bar.current && window.scrollY <= 8) {
          document.documentElement.style.setProperty("--hdr-h", `${this.bar.current.offsetHeight}px`);
        }
      });
      this.ro.observe(this.bar.current);
    }
    this.onScroll();
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.glideHome);
    document.addEventListener("mousedown", this.onOutside);
    document.addEventListener("keydown", this.onKey);
    // link widths change once the web font lands; measuring before that puts
    // the pill on the fallback font's widths
    const fonts = document.fonts && document.fonts.ready;
    (fonts || Promise.resolve()).then(() => {
      if (this.unmounted) return;
      this.glideHome();
      // only now may the pill animate, so it does not slide in from the corner
      requestAnimationFrame(() => this.glide.current && this.glide.current.classList.add("is-ready"));
    });
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.ro) this.ro.disconnect();
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.glideHome);
    document.removeEventListener("mousedown", this.onOutside);
    document.removeEventListener("keydown", this.onKey);
  }

  onScroll = () => {
    const scrolled = window.scrollY > 8;
    if (scrolled !== this.state.scrolled) this.setState({ scrolled });
  };

  onOutside = (e) => {
    if (this.state.menuOpen && this.account.current && !this.account.current.contains(e.target)) {
      this.setState({ menuOpen: false });
    }
  };

  onKey = (e) => {
    if (e.key === "Escape" && this.state.menuOpen) this.setState({ menuOpen: false });
  };

  glideTo(el) {
    const pill = this.glide.current;
    if (!pill) return;
    if (!el) {
      pill.classList.remove("is-on");
      return;
    }
    pill.style.setProperty("--gx", el.offsetLeft + "px");
    pill.style.setProperty("--gw", el.offsetWidth + "px");
    pill.classList.add("is-on");
  }

  glideHome = () => {
    const nav = this.nav.current;
    this.glideTo(nav && nav.querySelector("a.active"));
  };

  onNavOver = (e) => {
    const link = e.target.closest && e.target.closest("a");
    if (link && this.nav.current.contains(link)) this.glideTo(link);
  };

  toggleMenu = () => {
    this.setState((s) => ({ menuOpen: !s.menuOpen }));
  };

  logout = (e) => {
    e.preventDefault();
    clearSession();
    window.location.href = "/";
  };

  toggleMobile = () => {
    this.setState((s) => ({ mobileOpen: !s.mobileOpen }));
  };

  closeMobile = () => {
    this.setState({ mobileOpen: false });
  };

  render() {
    const { tenant } = this.props;
    const { currentUser, mobileOpen, scrolled, menuOpen } = this.state;
    const teacherName = (tenant && tenant.name) || "";
    const downloadUrl = brandingOf(tenant).downloadUrl;
    const showAbout = hasAbout(tenant);
    // what the tenant category switches on: a gym site has no school lessons, a site without meetings no live page
    const showCourses = hasModule(tenant, "COURSES");
    const showGallery = hasModule(tenant, "GALLERY");
    const showLive = hasModule(tenant, "MEETINGS");
    const showPlans = hasModule(tenant, "MEMBERSHIPS");
    const showLibrary = hasModule(tenant, "LIBRARY");
    const showRecipes = hasModule(tenant, "RECIPES");
    const showCalendar = hasModule(tenant, "CALENDAR");

    // inside the dashboard's live preview the teacher already knows they are previewing
    const preview = tenant && !isEmbedded() ? tenant.themePreview : null;
    const memberLinks = currentUser && (showLive || showLibrary || showCalendar || showRecipes || showPlans);
    const userName = (currentUser && currentUser.fullName) || "";
    const firstName = userName.trim().split(/\s+/)[0] || "";

    const headerClass = [
      "site-header",
      currentUser ? "is-member" : "is-guest",
      scrolled ? "is-scrolled" : "",
      this.entering ? "is-entering" : "",
      mobileOpen ? "is-mobile-open" : "",
    ].filter(Boolean).join(" ");

    return (
      <>
      {preview && (
        <div className="theme-preview-bar" dir="rtl">
          <span>معاينة ثيم <b>{preview.nameAr}</b> — الشكل ده مش محفوظ ومحدش غيرك شايفه</span>
          <button type="button" onClick={endPreview}>إنهاء المعاينة</button>
        </div>
      )}
      <header className={headerClass} ref={this.bar}>
        <div className="nav-inner">
          <Link to="/" className="brand" onClick={this.closeMobile}>
            {tenant && tenant.image ? (
              <img className="logo-header" src={tenant.image} alt={teacherName} />
            ) : (
              <>
                <span className="brand-mark" aria-hidden="true">{initialsOf(teacherName)}</span>
                <span className="brand-name">{teacherName}</span>
              </>
            )}
          </Link>

          <nav
            className="pages has-glide"
            ref={this.nav}
            onMouseOver={this.onNavOver}
            onFocus={this.onNavOver}
            onMouseLeave={this.glideHome}
            onBlur={this.glideHome}
          >
            <span className="nav-glide" ref={this.glide} aria-hidden="true"></span>
            <NavLink exact activeClassName="active" to="/">{t("home")}</NavLink>
            {showCourses && <NavLink activeClassName="active" to="/courses">{t("courses")}</NavLink>}
            {showGallery && <NavLink activeClassName="active" to="/gallery">{t("gallery")}</NavLink>}
            {showAbout && <NavLink activeClassName="active" to="/about">{t("about")}</NavLink>}
            <NavLink activeClassName="active" to="/contact">{t("contact")}</NavLink>
            {memberLinks && <span className="nav-split" aria-hidden="true"></span>}
            {currentUser && showLive && (
              <NavLink activeClassName="active" to="/live" className="is-live">
                <span className="live-dot" aria-hidden="true"></span>
                {t("live")}
              </NavLink>
            )}
            {currentUser && showLibrary && <NavLink activeClassName="active" to="/library">{t("lib.library")}</NavLink>}
            {currentUser && showCalendar && <NavLink activeClassName="active" to="/calendar">{t("cal.title")}</NavLink>}
            {currentUser && showRecipes && <NavLink activeClassName="active" to="/recipes">{t("lib.recipes")}</NavLink>}
            {currentUser && showPlans && <NavLink activeClassName="active" to="/account">{t("myPlans")}</NavLink>}
          </nav>

          <div className="nav-right">
            {downloadUrl && (
              <a className="download-link" href={downloadUrl} download title={t("downloadApp")}>
                <Icon name="download" />
                <span>{t("downloadApp")}</span>
              </a>
            )}
            {currentUser ? (
              <div className={`account ${menuOpen ? "is-open" : ""}`} ref={this.account}>
                <button
                  type="button"
                  className="account-chip"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onClick={this.toggleMenu}
                >
                  <span className="avatar" aria-hidden="true">
                    <span className="avatar-ring"></span>
                    <span className="avatar-face">{initialsOf(userName) || <Icon name="user" />}</span>
                    <span className="avatar-dot"></span>
                  </span>
                  <span className="hi">{firstName}</span>
                  <Icon name="chevron" className="chev" />
                </button>

                <div className="account-menu" role="menu">
                  <div className="am-head">
                    <span className="am-hi">{t("hi", { name: userName })}</span>
                    {currentUser.email && <span className="am-mail" dir="ltr">{currentUser.email}</span>}
                  </div>
                  {showPlans && (
                    <Link className="am-item" role="menuitem" to="/account">
                      <Icon name="award" />
                      <span>{t("myPlans")}</span>
                    </Link>
                  )}
                  <button type="button" className="am-item am-out" role="menuitem" onClick={this.logout}>
                    <Icon name="logout" />
                    <span>{t("logout")}</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="guest-box">
                <Link className="ghost-btn" to="/sign-in">{t("login")}</Link>
                <Link className="login-btn" to="/sign-up">
                  <span>{t("createAccount")}</span>
                  <Icon name="arrow" className="go" />
                </Link>
              </div>
            )}
            <button
              className={`nav-toggle ${mobileOpen ? "open" : ""}`}
              aria-label={t("openMenu")}
              aria-expanded={mobileOpen}
              onClick={this.toggleMobile}
            >
              <span className="bar"></span>
              <span className="bar"></span>
              <span className="bar"></span>
            </button>
          </div>
        </div>

        <div className={`mobile-menu ${mobileOpen ? "open" : ""}`}>
          {currentUser ? (
            <div className="mm-card">
              <span className="avatar" aria-hidden="true">
                <span className="avatar-ring"></span>
                <span className="avatar-face">{initialsOf(userName) || <Icon name="user" />}</span>
                <span className="avatar-dot"></span>
              </span>
              <span className="mm-who">
                <b>{t("hi", { name: userName })}</b>
                {currentUser.email && <span dir="ltr">{currentUser.email}</span>}
              </span>
            </div>
          ) : (
            <div className="mm-cta">
              <Link className="ghost-btn" to="/sign-in" onClick={this.closeMobile}>{t("login")}</Link>
              <Link className="login-btn" to="/sign-up" onClick={this.closeMobile}>{t("createAccount")}</Link>
            </div>
          )}
          <NavLink exact activeClassName="active" to="/" onClick={this.closeMobile}>{t("home")}</NavLink>
          {showCourses && <NavLink activeClassName="active" to="/courses" onClick={this.closeMobile}>{t("courses")}</NavLink>}
          {showGallery && <NavLink activeClassName="active" to="/gallery" onClick={this.closeMobile}>{t("gallery")}</NavLink>}
          {showAbout && <NavLink activeClassName="active" to="/about" onClick={this.closeMobile}>{t("about")}</NavLink>}
          <NavLink activeClassName="active" to="/contact" onClick={this.closeMobile}>{t("contact")}</NavLink>
          {currentUser && showLive && (
            <NavLink activeClassName="active" to="/live" className="is-live" onClick={this.closeMobile}>
              <span className="live-dot" aria-hidden="true"></span>
              {t("live")}
            </NavLink>
          )}
          {currentUser && showLibrary && <NavLink activeClassName="active" to="/library" onClick={this.closeMobile}>{t("lib.library")}</NavLink>}
          {currentUser && showCalendar && <NavLink activeClassName="active" to="/calendar" onClick={this.closeMobile}>{t("cal.title")}</NavLink>}
          {currentUser && showRecipes && <NavLink activeClassName="active" to="/recipes" onClick={this.closeMobile}>{t("lib.recipes")}</NavLink>}
          {currentUser && showPlans && <NavLink activeClassName="active" to="/account" onClick={this.closeMobile}>{t("myPlans")}</NavLink>}
          {downloadUrl && <a href={downloadUrl} download>{t("downloadApp")}</a>}
          {currentUser && (
            <button className="mm-auth" onClick={this.logout}>
              <Icon name="logout" />
              {t("logout")}
            </button>
          )}
        </div>
      </header>
      </>
    );
  }
}

export default Header;
